import { useDisclosure } from '@mantine/hooks';
import { ListBox } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import type { EditBlockStore } from '../../store';
import { getEditActionLabel } from '../../utils';
import { EditActionValueDropdown } from './EditActionValueDropdown';
import { EditTarget } from './EditTarget';

export interface EditActionProps {
	actionSearchKey: string;
	store: EditBlockStore;
}

export const EditAction = observer(
	({ actionSearchKey, store }: EditActionProps) => {
		const [opened, { close, toggle, open }] = useDisclosure(false);
		const editAction = store.editActions.get(actionSearchKey)!;

		const handleOnOpenChange = useCallback(
			(newOpen: boolean) => {
				if (newOpen) {
					open();
				} else {
					close();
				}
			},
			[close, open]
		);

		const typeLabel = getEditActionLabel(editAction.editAction.type);

		const handleSetEditActionValue = useCallback(
			(value: string | boolean, shouldClose: boolean, label?: string) => {
				editAction.setValue(value);
				editAction.setDisplay(label ?? '');

				if (shouldClose) {
					close();
				}
			},
			[close, editAction]
		);

		return (
			<ListBox
				closeOnEscape={false}
				opened={opened}
				onOpenChange={handleOnOpenChange}
				autoAdjustPosition={false}
			>
				<ListBox.Target>
					<EditTarget
						isMenuOpen={opened}
						label={typeLabel}
						onClear={() => store.deleteEditAction(actionSearchKey)}
						onToggle={toggle}
						store={store}
						actionSearchKey={actionSearchKey}
					/>
				</ListBox.Target>
				{opened && (
					<EditActionValueDropdown
						store={store}
						actionSearchKey={actionSearchKey}
						onItemClick={handleSetEditActionValue}
					/>
				)}
			</ListBox>
		);
	}
);
